<template>
    <v-card :loading="loading">
        <v-card-title class="text-h5">Update Gross Mass</v-card-title>
        <v-divider class="ml-4 mr-5 mb-2" />
        <v-card-text>
            <v-form v-model="valid">
                <v-text-field
                    v-model="grossMass"
                    label="Gross Mass"
                    type="number"
                    required
                    :rules="[rules.required, rules.number, rules.minimum]"
                    suffix="Kg"
                    :hint="`Minimum: ${minGrossMass.toFixed(1)} Kg`"
                    persistent-hint
                />
            </v-form>
        </v-card-text>
        <v-card-actions class="pb-3 pt-0">
            <v-btn text color="secondary" @click="close">
                Close
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
                color="primary"
                @click="updateGrossMass"
                :loading="loading"
                rounded
                :disabled="!valid || !hasChanged"
            >
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'

export default {
    name: 'UpdatePackageGrossMass',
    props: {
        packageElement: {
            type: Object,
            required: true,
        },
        packingListId: {
            type: String,
            required: true,
        },
        projectId: {
            type: String,
            required: true,
        },
        onlySuggested: {
            type: Boolean,
            required: true,
            default: false,
        },
    },
    data() {
        return {
            valid: false,
            loading: false,
            grossMass: '',
            minGrossMass: 0,
            originalGrossMass: 0,
            rules: {
                required: v => !!v || 'Required',
                number: v => v >= 0 || 'The value must be positive',
                minimum: v =>
                    v >= this.minGrossMass ||
                    `Must be at least ${this.minGrossMass.toFixed(1)} Kg`,
            },
        }
    },
    computed: {
        hasChanged() {
            return Number(this.grossMass) !== this.originalGrossMass
        },
    },
    mounted() {
        this.originalGrossMass = this.packageElement.grossMass || 0
        this.grossMass = this.originalGrossMass.toString()
        this.calculateMinGrossMass()
    },
    watch: {
        packageElement: {
            handler() {
                this.calculateMinGrossMass()
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        close() {
            this.$emit('closeDialog', {})
        },
        calculateMinGrossMass() {
            this.minGrossMass = 0
            if (
                this.packageElement.items &&
                this.packageElement.items.length > 0
            ) {
                this.packageElement.items.forEach(item => {
                    if (item.mass) {
                        if (this.onlySuggested) {
                            this.minGrossMass +=
                                Number(item.suggestedQty) * Number(item.mass)
                        } else {
                            this.minGrossMass +=
                                Number(item.packingQty) * Number(item.mass)
                        }
                    }
                })
            }
        },
        async updateGrossMass() {
            try {
                this.loading = true
                const updatedPackages = await API.updatePackageGrossMass({
                    projectId: this.projectId,
                    packingListId: this.packingListId,
                    packageId: this.packageElement.id,
                    grossMass: Number(this.grossMass),
                })

                // Find the updated package from the response
                const updatedPackage = updatedPackages.find(
                    pkg => pkg.id === this.packageElement.id
                )

                // Ensure we pass the updated package dimensions even if mass is 0
                const packageDimensions =
                    updatedPackage?.packageDimensions ||
                    this.packageElement.packageDimensions

                this.$emit('closeDialog', {
                    packageDimensions: {
                        ...packageDimensions,
                        mass:
                            packageDimensions?.mass !== undefined
                                ? packageDimensions.mass
                                : 0,
                    },
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style>
#virtual-scroll-table {
    width: 100%;
    overflow: auto;
}
.v-data-table {
    border: 1px solid #eeeeee;
}
</style>
